import React, {
    Dispatch,
    ReactElement,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {Nav, Col, Row} from 'react-bootstrap';
import {isDeviceSmall, useTabletSize} from 'shared/helpers/DeviceSize';
import {scrollNearestScrollableToTop} from 'shared/helpers/scrollNearestScrollableToTop';
import {Section, WizardNavLink} from 'shared/components/Wizard/WizardNavLink';

import './Wizard.scss';
import styled from 'styled-components';

interface WizardProps {
    children: ReactElement<Section>[];
    currentTab: number;
    setCurrentTab: Dispatch<SetStateAction<number>>;
    tabErrors?: Record<number, number>;
    id?: string;
    className?: string;
    imageColumn?: React.JSX.Element;
    lazy?: boolean;
    inSizesTab?: boolean;
    wizardHeaderRef?: React.MutableRefObject<HTMLElement>;
    isMiniBrowser?: boolean;
    showNav?: number[];
    /** Mini-browser cabinets show icons when there are more than 3 tabs. */
    forceShowIcon?: boolean;
}

export const Wizard = ({
    children,
    currentTab,
    setCurrentTab,
    tabErrors,
    id,
    className,
    imageColumn,
    lazy = false,
    inSizesTab = false,
    wizardHeaderRef = null,
    isMiniBrowser = false,
    showNav,
    forceShowIcon = false,
}: WizardProps) => {
    const isSmallDevice = isDeviceSmall();
    const isTabletSize = useTabletSize();
    const [selectedTitle, setSelectedTitle] = useState('');
    const wizardBodyRef = useRef<HTMLDivElement>(null);

    const filterChildren = useCallback((child: ReactElement<Section>) => {
        if (child.props.hasOwnProperty('hide') && child.props.hide) {
            return false;
        }

        if (
            child.props.hasOwnProperty('visibility') &&
            !child.props.visibility
        ) {
            return false;
        }

        return true;
    }, []);

    const navigationData = useMemo<Section[]>(() => {
        if (Array.isArray(children)) {
            return children.filter(filterChildren).map((child, index) => {
                if (showNav && !showNav.includes(index)) {
                    return null;
                }

                return {
                    eventKey: child.props.eventKey,
                    title: child.props.title,
                    hasPadding: child.props.hasOwnProperty('noPadding')
                        ? !child.props.noPadding
                        : true,
                };
            });
        }
    }, [children, showNav]);

    const tabClick = useCallback((key: string) => {
        setCurrentTab(parseInt(key));
        scrollNearestScrollableToTop(wizardBodyRef.current);
    }, []);

    useEffect(() => {
        const currentSection = navigationData.find(
            (section) => section?.eventKey == currentTab
        );

        if (currentSection) {
            setSelectedTitle(currentSection.title);
        }
    }, [currentTab]);

    const [navigation, hasPadding, title] = useMemo(() => {
        {
            let hasPadding = true;
            let navigation = null;
            let title = '';

            if (navigationData.filter(Boolean).length === 0) {
                return [null, false, title];
            }

            if (!isSmallDevice || isMiniBrowser) {
                navigation = (
                    <Nav
                        style={isMiniBrowser ? {padding: '0 12px'} : {}}
                        fill
                        className="cbc-wizard"
                        id={id}>
                        {navigationData.map((child, index) => {
                            if (child === null) return;

                            const errors =
                                tabErrors && tabErrors[Number(index)];

                            if (currentTab == child.eventKey) {
                                hasPadding = child.hasPadding;
                                title = child.title;
                            }

                            return (
                                <WizardNavLink
                                    currentTab={currentTab}
                                    key={child.title}
                                    errors={errors}
                                    section={child}
                                    showIcon={
                                        forceShowIcon ||
                                        (navigationData.filter(Boolean).length >
                                            3 &&
                                            isMiniBrowser)
                                    }
                                    tabClick={tabClick}
                                />
                            );
                        })}
                    </Nav>
                );
            } else {
                const currentChild = navigationData.find(
                    (navigationDatum) => navigationDatum?.eventKey == currentTab
                );

                if (currentChild) {
                    hasPadding = currentChild.hasPadding;
                    title = currentChild.title;
                    navigation = (
                        <div
                            className="cbc-wizard-title"
                            ref={(el) => {
                                if (wizardHeaderRef && el) {
                                    wizardHeaderRef.current = el;
                                }
                            }}>
                            <span>{currentChild.title}</span>
                        </div>
                    );
                }
            }

            return [navigation, hasPadding, title];
        }
    }, [
        navigationData,
        isSmallDevice,
        isMiniBrowser,
        tabClick,
        currentTab,
        tabErrors,
        forceShowIcon,
        id,
        wizardHeaderRef,
    ]);

    const showDynamicColumns = (inSizesTab: boolean) => {
        const filteredChildren = children
            .filter(filterChildren)
            .filter((child) => {
                if (lazy && child.props.eventKey != currentTab) {
                    return false;
                }
                return true;
            });

        const displayChild = (child: ReactElement<Section>) => {
            const display = child.props.eventKey == currentTab;
            return React.cloneElement(child, {
                className: !display ? 'section-hidden' : '',
            });
        };

        if (inSizesTab === true) {
            return (
                <Row>
                    {imageColumn ? (
                        <Col
                            md={isTabletSize ? 4 : 3}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                            }}>
                            {imageColumn}
                        </Col>
                    ) : null}
                    <Col
                        md={imageColumn ? (isTabletSize ? 8 : 9) : 12}
                        style={
                            isTabletSize
                                ? {paddingLeft: 0, paddingRight: 0}
                                : {}
                        }>
                        {filteredChildren.map(displayChild)}
                    </Col>
                </Row>
            );
        } else {
            return <>{filteredChildren.map(displayChild)}</>;
        }
    };

    return (
        <>
            {navigation}
            {(forceShowIcon || navigationData.filter(Boolean).length > 3) &&
            isMiniBrowser &&
            selectedTitle ? (
                <Header>{selectedTitle}</Header>
            ) : null}
            <WizardBody
                ref={wizardBodyRef}
                $noPadding={typeof showNav !== 'undefined'}
                style={hasPadding ? {} : {paddingLeft: 0, paddingRight: 0}}
                className={
                    typeof className !== 'undefined'
                        ? `cbc-wizard-content ${className} ${title}`
                        : `cbc-wizard-content ${title}`
                }>
                {showDynamicColumns(inSizesTab)}
            </WizardBody>
        </>
    );
};

const WizardBody = styled.div<{$noPadding: boolean}>`
    ${({$noPadding}) => {
        if ($noPadding) {
            return `padding: 0 !important;`;
        }
    }}
`;

const Header = styled.h1`
    margin: 15px 12px 0 !important;
    padding: 2px 10px !important;
`;
